import type { ReactNode } from 'react';
import {
  LayoutDashboard,
  Package,
  MapPin,
  Wallet,
  Users,
  FileText,
  Settings,
  Gavel,
  BarChart3,
  ShieldCheck,
  Building,
  Megaphone,
  Route,
  Map,
  Shield,
  MessageSquare,
  Mail,
} from 'lucide-react';
import type { BackofficeUserType } from './theme';

export interface NavItem {
  key: string;
  label: string;
  icon: ReactNode;
  path: string;
  badge?: number;
}

const clientNavItems: NavItem[] = [
  { key: 'dashboard', label: 'Tableau de bord', icon: <LayoutDashboard size={20} />, path: '/dashboard/client' },
  { key: 'orders', label: 'Commandes', icon: <Package size={20} />, path: '/dashboard/client/commandes', badge: 3 },
  { key: 'tracking', label: 'Suivi en direct', icon: <MapPin size={20} />, path: '/dashboard/client/suivi' },
  { key: 'addresses', label: 'Mes adresses', icon: <Map size={20} />, path: '/dashboard/client/adresses' },
  { key: 'community', label: 'Voisins disponibles', icon: <Users size={20} />, path: '/dashboard/client/voisins' },
  { key: 'wallet', label: 'Portefeuille', icon: <Wallet size={20} />, path: '/dashboard/client/portefeuille' },
  { key: 'history', label: 'Historique', icon: <FileText size={20} />, path: '/dashboard/client/historique' },
];

const proDriverNavItems: NavItem[] = [
  { key: 'dashboard', label: 'Tableau de bord', icon: <LayoutDashboard size={20} />, path: '/dashboard/livreur-pro' },
  { key: 'missions', label: 'Missions', icon: <Package size={20} />, path: '/dashboard/livreur-pro/missions', badge: 5 },
  { key: 'route', label: 'Feuille de route', icon: <Route size={20} />, path: '/dashboard/livreur-pro/itineraire' },
  { key: 'earnings', label: 'Revenus', icon: <Wallet size={20} />, path: '/dashboard/livreur-pro/revenus' },
  { key: 'zones', label: 'Mes zones', icon: <Map size={20} />, path: '/dashboard/livreur-pro/zones' },
  { key: 'documents', label: 'Documents', icon: <FileText size={20} />, path: '/dashboard/livreur-pro/documents' },
  { key: 'insurance', label: 'Assurance', icon: <Shield size={20} />, path: '/dashboard/livreur-pro/assurance' },
];

const communityDriverNavItems: NavItem[] = [
  { key: 'dashboard', label: 'Tableau de bord', icon: <LayoutDashboard size={20} />, path: '/dashboard/livreur' },
  { key: 'missions', label: 'Missions', icon: <Package size={20} />, path: '/dashboard/livreur/missions', badge: 2 },
  { key: 'trips', label: 'Mes trajets', icon: <Route size={20} />, path: '/dashboard/livreur/trajets' },
  { key: 'earnings', label: 'Gains', icon: <Wallet size={20} />, path: '/dashboard/livreur/gains' },
  { key: 'documents', label: 'Mes infos', icon: <FileText size={20} />, path: '/dashboard/livreur/infos' },
];

const adminNavItems: NavItem[] = [
  { key: 'dashboard', label: 'Tableau de bord', icon: <LayoutDashboard size={20} />, path: '/dashboard/admin' },
  { key: 'users', label: 'Utilisateurs', icon: <Users size={20} />, path: '/dashboard/admin/utilisateurs' },
  { key: 'orders', label: 'Commandes', icon: <Package size={20} />, path: '/dashboard/admin/commandes' },
  { key: 'disputes', label: 'Litiges', icon: <Gavel size={20} />, path: '/dashboard/admin/litiges' },
  { key: 'documents', label: 'Documents', icon: <FileText size={20} />, path: '/dashboard/admin/documents' },
  { key: 'support', label: 'Support', icon: <MessageSquare size={20} />, path: '/dashboard/admin/support' },
  { key: 'communication', label: 'Communication', icon: <Mail size={20} />, path: '/dashboard/admin/communication' },
  { key: 'config', label: 'Configuration', icon: <Settings size={20} />, path: '/dashboard/admin/configuration' },
  { key: 'infra', label: 'Infrastructures', icon: <Building size={20} />, path: '/dashboard/admin/infrastructures' },
  { key: 'geography', label: 'Géographie', icon: <Map size={20} />, path: '/dashboard/admin/geographie' },
  { key: 'promotions', label: 'Promotions', icon: <Megaphone size={20} />, path: '/dashboard/admin/promotions' },
  { key: 'reports', label: 'Rapports', icon: <BarChart3 size={20} />, path: '/dashboard/admin/rapports' },
];

const superAdminNavItems: NavItem[] = [
  { key: 'dashboard', label: 'Tableau de bord', icon: <LayoutDashboard size={20} />, path: '/dashboard/super-admin' },
  { key: 'admins', label: 'Administrateurs', icon: <ShieldCheck size={20} />, path: '/dashboard/super-admin/administrateurs' },
  { key: 'users', label: 'Utilisateurs', icon: <Users size={20} />, path: '/dashboard/super-admin/utilisateurs' },
  { key: 'config', label: 'Configuration', icon: <Settings size={20} />, path: '/dashboard/super-admin/configuration' },
  { key: 'zones', label: 'Infrastructures', icon: <Map size={20} />, path: '/dashboard/super-admin/zones' },
  { key: 'geography', label: 'Géographie', icon: <Map size={20} />, path: '/dashboard/super-admin/geographie' },
  { key: 'pricing', label: 'Tarification', icon: <Wallet size={20} />, path: '/dashboard/super-admin/tarification' },
  { key: 'reports', label: 'Rapports globaux', icon: <BarChart3 size={20} />, path: '/dashboard/super-admin/rapports' },
  { key: 'communication', label: 'Communication', icon: <Mail size={20} />, path: '/dashboard/super-admin/communication' },
];

const relayPointNavItems: NavItem[] = [
  { key: 'dashboard', label: 'Tableau de bord', icon: <LayoutDashboard size={20} />, path: '/dashboard/point-relais' },
  { key: 'packages', label: 'Colis', icon: <Package size={20} />, path: '/dashboard/point-relais/colis' },
  { key: 'activities', label: 'Activités', icon: <FileText size={20} />, path: '/dashboard/point-relais/activites' },
];

export const DASHBOARD_BASE: Record<BackofficeUserType, string> = {
  client: '/dashboard/client',
  pro_driver: '/dashboard/livreur-pro',
  community_driver: '/dashboard/livreur',
  relay_point: '/dashboard/point-relais',
  admin: '/dashboard/admin',
  super_admin: '/dashboard/super-admin',
};

export function getDashboardBase(userType: BackofficeUserType): string {
  return DASHBOARD_BASE[userType];
}

export function getNavItems(userType: BackofficeUserType): NavItem[] {
  switch (userType) {
    case 'client':
      return clientNavItems;
    case 'pro_driver':
      return proDriverNavItems;
    case 'community_driver':
      return communityDriverNavItems;
    case 'relay_point':
      return relayPointNavItems;
    case 'admin':
      return adminNavItems;
    case 'super_admin':
      return superAdminNavItems;
    default:
      return clientNavItems;
  }
}
